import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import TableCell from "@mui/material/TableCell";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import { Cell, CustomChipStatus, CustomIconButtonDemo, CustomTable, CustomTableHead, HeaderCell, TableContainer, TableContent, TableHeight } from "../../../../pages/styles";
import { Divider, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";

import {
  ContainerActions,
} from "./styles";

import { ColorsContext } from "../../../../Context/ColorsContext";
import { FormatDate } from "../../../../utils/formatDate";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PeopleIcon from '@mui/icons-material/People';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

// Utils
import { useTranslation } from "react-i18next";

//Hooks
import { useSortByColumn } from "../../../../Hooks/useSortByColumn";
import { MediaQuerys } from "../../../../styles/GlobalStyle";

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from "../../../../Context/PermissionsContext";

import { CancelRounded, CheckCircleRounded } from "@mui/icons-material";

const headCells = [
  {
    id: "id",
    center: false,
    disablePadding: true,
    sortArrow: true,
    sticky: true,
    direction: 'left',
    label: "system.permissions.table.id",
  },
  {
    id: "name",
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: "system.permissions.table.names",
  },
  {
    id: "description",
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: "system.permissions.table.description",
  },
  {
    id: "isExternal",
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: "system.permissions.table.isExternal",
    customSort: 'isExternal ? "Si" : "No"',
  },
  {
    id: "isRestricted",
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: "system.permissions.table.isRestricted",
    customSort: 'isRestricted ? "Si" : "No"',
  },
  {
    id: "status",
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: "system.permissions.table.status",
    customSort: 'isActive ? "Activo" : "Inativo"',
  },
  {
    id: "created_at",
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: "system.permissions.table.createdDate",
    customSort: 'createdAt',
  },
  {
    id: "actions",
    center: true,
    disablePadding: true,
    sortArrow: false,
    label: "common.actions.title",
    sticky: true,
    direction: 'right',
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const { t } = useTranslation();
  const { colors } = React.useContext(ColorsContext);

  const createSortHandler = (property, customSort) => (event) => {
    onRequestSort(event, property, customSort);
  };

  return (
    <CustomTableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <HeaderCell
            key={headCell.id}
            align={headCell.center ? "center" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            dataHead={headCell}
          >
            {headCell.sortArrow == true && (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id, headCell.customSort)}
              >
                {t(headCell.label) || headCell.label}
                {orderBy === headCell.id ? <Box component="span"></Box> : null}
              </TableSortLabel>
            )}
            {headCell.sortArrow == false && (
              <>
                <Box component="span">{t(headCell.label) || headCell.label}</Box>
              </>
            )}
          </HeaderCell>
        ))}
      </TableRow>
    </CustomTableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function TableRoles({
  filteredRequest,
  rows,
  selectedRole,
}) {

  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const { format } = new FormatDate();
  const { t } = useTranslation();
  const { mobile } = MediaQuerys;
  const { colors } = React.useContext(ColorsContext);
  const ability = useAbility(AbilityContext);

  const { order, orderBy, stableSort, getComparator, handleRequestSort } = useSortByColumn('createdAt');

  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [openMenuId, setOpenMenuId] = React.useState(null);

  const handleOpenMenu = (event, id) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setOpenMenuId(id);
  };

  const handleCloseMenu = (event) => {
    if (event) event.stopPropagation();
    setMenuAnchorEl(null);
    setOpenMenuId(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box
      sx={{ width: "100%", marginTop: "16px", [mobile]: { marginTop: "15px" } }}
    >
      <TableContainer sx={{ width: "100%", mb: 2 }}>
        <TableHeight>
          <CustomTable
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableContent>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
              rows.sort(getComparator(order, orderBy)).slice() */}
              {stableSort(filteredRequest, getComparator())
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                    <TableRow hover tabIndex={-1} key={row.id}>
                      <Cell
                        component="th"
                        scope="row"
                        sx={{ border: "none" }}
                        direction='left'
                      >
                        {row.id}
                      </Cell>
                      <Cell sx={{ border: "none" }}>{row.name}</Cell>
                      <Cell sx={{ border: "none" }}>
                        {row.description}
                      </Cell>
                      <Cell sx={{ border: "none" }}>
                        {row.isExternal ? t("common.yes") : t("common.no")}
                      </Cell>
                      <Cell sx={{ border: "none" }}>
                        {row.isRestricted ? t("common.yes") : t("common.no")}
                      </Cell>
                      <Cell sx={{ border: "none" }}>
                        <CustomChipStatus 
                          enabled={row.isActive} 
                          icon={row.isActive ? <CheckCircleRounded /> : <CancelRounded />}
                          label={row.isActive ? t('system.permissions.table.active') : t('system.permissions.table.inactive')}
                        />
                      </Cell>
                      <Cell sx={{ border: "none" }}>
                        {format({ date: row.createdAt, format: true})}
                      </Cell>

                      <Cell direction='right'>

                        <ContainerActions>
                          <Tooltip
                            title={t(
                              "system.permissions.table.assignedPermissions"
                            )}
                          >
                            <CustomIconButtonDemo
                              size="small"
                              btnType="primary"
                              aria-label="detail"
                              onClick={() => selectedRole(row, "VIEW")}
                            >
                              <VisibilityIcon />
                            </CustomIconButtonDemo>
                          </Tooltip>

                          <CustomIconButtonDemo
                            size="small"
                            btnType=""
                            style={{ backgroundColor: '#dbdbdb', color: colors.buttonActionColor }}
                            onClick={(e) => handleOpenMenu(e, row.id)}
                          >
                            <MoreHorizIcon />
                          </CustomIconButtonDemo>
                          <Menu
                            anchorEl={menuAnchorEl}
                            open={openMenuId === row.id}
                            onClose={handleCloseMenu}
                            onClick={handleCloseMenu}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                              elevation: 0,
                              sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                mt: 1.5,
                                '&:before': {
                                  content: '""',
                                  display: 'block',
                                  position: 'absolute',
                                  top: 0,
                                  right: 14,
                                  width: 10,
                                  height: 10,
                                  bgcolor: 'background.paper',
                                  transform: 'translateY(-50%) rotate(45deg)',
                                  zIndex: 0,
                                },
                              },
                            }}
                          >

                            {row.isActive && ability.can("UpdateRole") && (
                              <MenuItem onClick={(e) => { e.stopPropagation(); selectedRole(row, "UPDATE"); handleCloseMenu(); }}>
                                <ListItemIcon>
                                  <EditIcon fontSize="small" style={{ color: colors.buttonActionColor }} />
                                </ListItemIcon>
                                <ListItemText>{t("system.permissions.table.editRole")}</ListItemText>
                              </MenuItem>
                            )}

                            {!row.isActive && ability.can("ToggleStateRole") && (
                              <MenuItem onClick={(e) => { e.stopPropagation(); selectedRole(row, "ACTIVATE"); handleCloseMenu(); }}>
                                <ListItemIcon>
                                  <CheckIcon fontSize="small" style={{ color: colors.buttonActionColor }} />
                                </ListItemIcon>
                                <ListItemText>{t("system.permissions.table.activateRole")}</ListItemText>
                              </MenuItem>
                            )}

                            {(ability.can("PermissionsList") && ability.can("FindPermissionsByRole")) && (
                              <MenuItem onClick={(e) => { e.stopPropagation(); selectedRole(row, "ASSIGN"); handleCloseMenu(); }}>
                                <ListItemIcon>
                                  <LockOpenIcon fontSize="small" style={{ color: colors.buttonActionColor }} />
                                </ListItemIcon>
                                <ListItemText>{t("system.permissions.table.assignPermissions")}</ListItemText>
                              </MenuItem>
                            )}

                            {ability.can("GetRelationRoles") && (
                              <MenuItem onClick={(e) => { e.stopPropagation(); selectedRole(row, "RELATE"); handleCloseMenu(); }}>
                                <ListItemIcon>
                                  <PeopleIcon fontSize="small" style={{ color: colors.buttonActionColor }} />
                                </ListItemIcon>
                                <ListItemText>{t("system.permissions.table.relatePermissions")}</ListItemText>
                              </MenuItem>
                            )}

                            {row.isActive && ability.can("ToggleStateRole") && (
                              <>
                                <Divider sx={{ borderColor: '#bebfbf', my: 0 }} />
                                <MenuItem onClick={(e) => { e.stopPropagation(); selectedRole(row, "DEACTIVATE"); handleCloseMenu(); }}>
                                  <ListItemIcon>
                                    <DeleteIcon fontSize="small" style={{ color: '#ef4646' }} />
                                  </ListItemIcon>
                                  <ListItemText sx={{ color: '#ef4646' }}>{t("system.permissions.table.inactivateRole")}</ListItemText>
                                </MenuItem>
                              </>
                            )}
                          </Menu>
                        </ContainerActions>
                      </Cell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableContent>
          </CustomTable>
        </TableHeight>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t("common.itemsPerPage")}
          labelDisplayedRows={({ from, to, count }) => {
            return "" + from + "-" + to + ` ${t("common.of")} ` + count;
          }}
          sx={{
            borderTop: "1px solid " + colors.gray,
          }}
        />
      </TableContainer>
    </Box>
  );
}
